import type { NotificationKind, NotificationPreference } from '@mockd/shared';
import { useNotificationPreferences } from '../../hooks';
import { Loading } from '../common';

const GROUPS: { title: string; kinds: { kind: NotificationKind; label: string; description: string }[] }[] = [
  {
    title: 'Usage',
    kinds: [
      { kind: 'usage_80', label: '80% of daily requests', description: 'A project has used 80% of its daily limit.' },
      { kind: 'usage_95', label: '95% of daily requests', description: 'A project is about to hit its daily limit.' },
      { kind: 'usage_limit_reached', label: 'Daily limit reached', description: 'Requests are being rejected until midnight UTC.' },
      { kind: 'rate_limited', label: 'Rate limited', description: 'An endpoint is rejecting requests over its per-minute limit.' },
    ],
  },
  {
    title: 'Endpoint health',
    kinds: [
      { kind: 'action_failed', label: 'HTTP action failed', description: "A rule's HTTP request action got an error or no response." },
      { kind: 'traffic_spike', label: 'Traffic spike', description: 'A project got far more requests than usual in a day.' },
      { kind: 'traffic_drop', label: 'Traffic drop', description: 'A busy project got far fewer requests than usual in a day.' },
    ],
  },
];

interface NotificationPreferencesProps {
  emailVerified: boolean;
}

export const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ emailVerified }) => {
  const { preferences, loading, update } = useNotificationPreferences();
  const byKind = new Map(preferences.map((p) => [p.kind, p]));

  const toggle = (pref: NotificationPreference, channel: 'inApp' | 'email') =>
    update({ ...pref, [channel]: !pref[channel] });

  return (
    <div id="notifications" className="card bg-base-100 scroll-mt-20">
      <div className="card-body">
        <h2 className="card-title text-lg">Notifications</h2>
        <p className="text-sm text-base-content/70">
          Choose which alerts about your projects you get, in the bell and by email.
        </p>
        {!emailVerified && (
          <p className="text-sm text-warning">Email alerts go to verified addresses only. Verify your email to get them.</p>
        )}

        {loading ? (
          <Loading className="py-6" />
        ) : (
          <div className="overflow-x-auto mt-2">
            <table className="table table-sm">
              {GROUPS.map((group) => (
                <tbody key={group.title}>
                  <tr>
                    <th scope="col" className="text-xs uppercase text-base-content/60">{group.title}</th>
                    <th scope="col" className="w-20 text-center text-xs text-base-content/60">In-app</th>
                    <th scope="col" className="w-20 text-center text-xs text-base-content/60">Email</th>
                  </tr>
                  {group.kinds.map(({ kind, label, description }) => {
                    const pref = byKind.get(kind);
                    if (!pref) return null;
                    return (
                      <tr key={kind}>
                        <td>
                          <span className="text-sm block">{label}</span>
                          <span className="text-xs text-base-content/60 block">{description}</span>
                        </td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary checkbox-sm"
                            checked={pref.inApp}
                            onChange={() => toggle(pref, 'inApp')}
                            aria-label={`${label}: in-app`}
                          />
                        </td>
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="checkbox checkbox-primary checkbox-sm"
                            checked={pref.email}
                            onChange={() => toggle(pref, 'email')}
                            aria-label={`${label}: email`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
